import React from 'react'
import BleedFrame from '@/components/app/document/overlays/bleed-frame'
import LiveArea from '@/components/app/document/overlays/live-area'
import ColumnsRowsOverlay from '@/components/app/document/overlays/columns-rows-overlay'
import { InnerTrimBleedFrame } from '../print-bleed'
import { PRINT_BLEED_MARGIN_PT } from '@/lib/document/print-bleed'

type SinglePageDocumentProps = {
  /** Full spread bleed width (both pages + bleed on both sides). */
  bleedWidth?: number
  bleedHeight?: number
  /** Single page trim width (not the full spread). */
  trimWidth?: number
  trimHeight?: number
  bleedMargin?: number
  /** Which side of the spread the page sits on. */
  side?: 'left' | 'right'
  marginLeft?: number
  marginRight?: number
  marginTop?: number
  marginBottom?: number
  showPrinterMarks?: boolean
  columns?: number
  rows?: number
  colGutter?: number
  rowGutter?: number
  showGuides?: boolean
  scale?: number
  tiledGridOverlay?: React.ReactNode
  /**
   * Page background, painted right above the paper and below everything else in the frame. Given
   * in spread canvas pts (not this frame's local space) — offset back by `wrapperLeft` here.
   */
  pageBackground?: React.ReactNode
  /**
   * When true with `showInnerTrimBleedFrame`, draws spine-only in-trim L-ring here (same role as
   * `showPrintBleed` on `DoubleSpreadDocument`). Full yellow bleed + labels live in
   * `SinglePageBleedOverlayAboveShapes` in the editor so they sit above shapes.
   */
  showPrintBleed?: boolean
  /**
   * In-trim L-frame in this tree. Set false when the editor’s above-shapes bleed overlay is active
   * so the inner band is not doubled.
   */
  showInnerTrimBleedFrame?: boolean
}

/**
 * Renders a single page with bleed, trim frame, margins, live area,
 * and column/row guides. Used for first and last pages of a book
 * which are displayed as standalone single pages (not as part of a spread).
 *
 * The page is rendered at `pageTrimWidth + 2 * bleedMargin` width (trim + bleed on spine and
 * outer edge) and positioned on the specified side of the full spread-width container so the
 * spine-side bleed extends into the empty half.
 *
 * Print bleed yellow bands + copy are not drawn here in the editor — they are painted in
 * `SinglePageBleedOverlayAboveShapes` above shapes (same pattern as `DoubleSpreadDocument` +
 * `DoubleSpreadBleedOverlayAboveShapes`).
 */
const SinglePageDocument: React.FC<SinglePageDocumentProps> = ({
  bleedWidth: spreadBleedWidth = 636,
  bleedHeight = 816,
  trimWidth = 612,
  trimHeight = 792,
  bleedMargin = PRINT_BLEED_MARGIN_PT,
  side = 'left',
  marginLeft = 24,
  marginRight = 24,
  marginTop = 24,
  marginBottom = 24,
  columns = 0,
  rows = 0,
  colGutter = 24,
  rowGutter = 24,
  showGuides = true,
  scale = 1,
  tiledGridOverlay = null,
  pageBackground = null,
  showPrintBleed = false,
  showInnerTrimBleedFrame = true,
}) => {
  const halfSpreadCanvas = Math.floor(spreadBleedWidth / 2)
  /** One page: trim + outer bleed on both spine and fore edge (matches Figma Layout=Start/End). */
  const singlePageBleedWidth = trimWidth + 2 * bleedMargin
  const trimLeft = bleedMargin
  const liveLeft = trimLeft + marginLeft
  const liveTop = bleedMargin + marginTop
  const liveWidth = trimWidth - marginLeft - marginRight
  const liveHeight = trimHeight - marginTop - marginBottom
  const wrapperLeft = side === 'left' ? 0 : halfSpreadCanvas - bleedMargin
  /**
   * `trimLeft` (= `bleedMargin`) is a fixed local offset regardless of `side` — which local edge
   * ends up touching the canvas midpoint (the spine) depends on which way `wrapperLeft` shifts the
   * whole frame. For `side="right"` (first page) the spine is the local-left margin; for
   * `side="left"` (last page) it's the local-right margin. That margin is a virtual "no facing
   * page here" gap, not a real bleed edge, so the frame's own background must not cover it —
   * otherwise it reads as an unwanted strip past the dashed trim line whenever bleeds are shown.
   */
  const spineClipPath =
    side === 'right' ? `inset(0 0 0 ${bleedMargin}px)` : `inset(0 ${bleedMargin}px 0 0)`

  return (
    <div className="absolute top-0 z-[1]" style={{ left: wrapperLeft }}>
      <BleedFrame
        width={singlePageBleedWidth}
        height={bleedHeight}
        paintBackground={showPrintBleed}
        clipPath={spineClipPath}
      >
        {/* Page fill */}
        <div
          className="doc-page-fill"
          style={{
            left: trimLeft,
            top: bleedMargin,
            width: trimWidth,
            height: trimHeight,
          }}
        />

        {pageBackground ? (
          <div className="pointer-events-none absolute top-0" style={{ left: -wrapperLeft }}>
            {pageBackground}
          </div>
        ) : null}

        {showPrintBleed && showInnerTrimBleedFrame && trimWidth > 0 && trimHeight > 0 && (
          <InnerTrimBleedFrame
            trimLeft={trimLeft}
            trimTop={bleedMargin}
            trimWidth={trimWidth}
            trimHeight={trimHeight}
            visible
          />
        )}

        {tiledGridOverlay}

        {/* Margin safe-area bands */}
        {marginTop > 0 && (
          <div
            className="doc-safe-area-band"
            style={{
              left: trimLeft,
              top: bleedMargin,
              width: trimWidth,
              height: marginTop,
              zIndex: 6,
            }}
          />
        )}
        {marginBottom > 0 && (
          <div
            className="doc-safe-area-band"
            style={{
              left: trimLeft,
              top: bleedMargin + trimHeight - marginBottom,
              width: trimWidth,
              height: marginBottom,
              zIndex: 6,
            }}
          />
        )}
        {marginLeft > 0 && (
          <div
            className="doc-safe-area-band"
            style={{
              left: trimLeft,
              top: bleedMargin + marginTop,
              width: marginLeft,
              height: liveHeight,
              zIndex: 6,
            }}
          />
        )}
        {marginRight > 0 && (
          <div
            className="doc-safe-area-band"
            style={{
              left: trimLeft + trimWidth - marginRight,
              top: bleedMargin + marginTop,
              width: marginRight,
              height: liveHeight,
              zIndex: 6,
            }}
          />
        )}

        {/* Live area */}
        <LiveArea left={liveLeft} top={liveTop} width={liveWidth} height={liveHeight} />

        {/* Column/row layout guides */}
        <ColumnsRowsOverlay
          trimLeft={liveLeft}
          trimTop={liveTop}
          trimWidth={liveWidth}
          trimHeight={liveHeight}
          columns={showGuides ? columns : 0}
          rows={showGuides ? rows : 0}
          colGutter={colGutter}
          rowGutter={rowGutter}
          scale={scale}
        />
      </BleedFrame>
    </div>
  )
}

export default SinglePageDocument
